import React, { useEffect, useRef, useState } from "react";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

export default function CsvUploadPage() {
  // --- Sales upload state ---
  const [salesFile, setSalesFile] = useState(null);
  const [salesUploading, setSalesUploading] = useState(false);
  const [salesUploadId, setSalesUploadId] = useState(null);
  const [salesStatus, setSalesStatus] = useState(null);
  const [salesError, setSalesError] = useState(null);
  const salesPollRef = useRef(null);

  // --- SKU master upload state ---
  const [skuFile, setSkuFile] = useState(null);
  const [skuUploading, setSkuUploading] = useState(false);
  const [skuUploadId, setSkuUploadId] = useState(null);
  const [skuStatus, setSkuStatus] = useState(null);
  const [skuError, setSkuError] = useState(null);
  const skuPollRef = useRef(null);

  // --- Shared polling hook ---
  const usePolling = (
    uploadId,
    setStatus,
    pollRef,
    endpoint,
    { appendStatus = true, stopCondition = () => false } = {}
  ) => {
    useEffect(() => {
      if (!uploadId) return;

      const poll = async () => {
        try {
          const token = localStorage.getItem("token");
          const statusSuffix = appendStatus ? "/status/" : "";
          const res = await fetch(`${BASE_URL}${endpoint}/${uploadId}${statusSuffix}`, {
            headers: { Authorization: `Token ${token}` },
          });
          const json = await res.json();
          setStatus(json);
          if (json.status === "completed" || json.status === "failed" || stopCondition(json)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (e) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };

      pollRef.current = setInterval(poll, 1500);
      poll(); // initial fetch

      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }, [uploadId]);
  };

  // Attach pollers
  usePolling(salesUploadId, setSalesStatus, salesPollRef, "/upload-status", {
    appendStatus: false,
  });
  usePolling(skuUploadId, setSkuStatus, skuPollRef, "/upload-status", {
    appendStatus: false,
  });

  // --- Upload handlers ---
  const handleSalesUpload = async (e) => {
    e.preventDefault();
    if (!salesFile) return setSalesError("Please choose a CSV file first.");

    try {
      setSalesUploading(true);
      setSalesError(null);
      setSalesStatus(null);

      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("file", salesFile);

      const res = await fetch(`${BASE_URL}/upload/sales/`, {
        method: "POST",
        headers: { Authorization: `Token ${token}` },
        body: form,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Upload failed");

      setSalesUploadId(json.upload_id);
    } catch (e) {
      setSalesError(e.message || "Upload failed");
    } finally {
      setSalesUploading(false);
    }
  };

  const handleSkuUpload = async (e) => {
    e.preventDefault();
    if (!skuFile) return setSkuError("Please choose a CSV file first.");

    try {
      setSkuUploading(true);
      setSkuError(null);
      setSkuStatus(null);

      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("file", skuFile);

      const res = await fetch(`${BASE_URL}/upload/sku/`, {
        method: "POST",
        headers: { Authorization: `Token ${token}` },
        body: form,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Upload failed");

      setSkuUploadId(json.upload_id);
    } catch (e) {
      setSkuError(e.message || "Upload failed");
    } finally {
      setSkuUploading(false);
    }
  };

  // --- Reusable progress renderer ---
  const UploadProgress = ({ uploadId, status }) => {
    const progress =
      status && status.total_rows
        ? Math.round(((status.processed_rows || 0) / status.total_rows) * 100)
        : status?.percent || 0;

    return (
      <div className="mt-5">
        <div className="flex justify-between text-sm text-gray-700">
          <div>Upload ID: {uploadId}</div>
          <div
            className={`font-medium ${
              status?.status === "failed"
                ? "text-red-600"
                : status?.status === "completed"
                ? "text-green-700"
                : "text-gray-700"
            }`}
          >
            Status: {status?.status || "queued"}
          </div>
        </div>

        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {status?.processed_rows ?? 0} / {status?.total_rows ?? 0} rows processed ({progress}%)
          </div>
          {status?.error_message && (
            <div className="mt-2 text-xs text-red-600">
              Error: {status.error_message}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 max-w-3xl mx-auto min-h-screen bg-gray-50">
      <Navbar title="Bulk CSV Uploads" />

      {/* Sales Upload Section */}
      <section className="bg-white rounded-lg shadow-sm border p-6 mt-4">
        <h1 className="text-xl font-semibold text-gray-900">Sales CSV Upload</h1>
        <p className="text-gray-600 mt-1">
          Upload large Sales CSV files (≈100k rows). Processed in the background.
        </p>

        <form className="mt-4" onSubmit={handleSalesUpload}>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setSalesFile(e.target.files?.[0])}
              disabled={salesUploading}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!salesFile || salesUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {salesUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
          {salesError && <p className="text-red-600 text-sm mt-2">{salesError}</p>}
        </form>

        {salesUploadId && <UploadProgress uploadId={salesUploadId} status={salesStatus} />}
      </section>

      {/* SKU Master Upload Section */}
      <section className="bg-white rounded-lg shadow-sm border p-6 mt-8">
        <h1 className="text-xl font-semibold text-gray-900">SKU Master Upload</h1>
        <p className="text-gray-600 mt-1">
          Upload or update SKU master data in bulk. The file is processed asynchronously.
        </p>

        <form className="mt-4" onSubmit={handleSkuUpload}>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setSkuFile(e.target.files?.[0])}
              disabled={skuUploading}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!skuFile || skuUploading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {skuUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
          {skuError && <p className="text-red-600 text-sm mt-2">{skuError}</p>}
        </form>

        {skuUploadId && <UploadProgress uploadId={skuUploadId} status={skuStatus} />}
      </section>
    </div>
  );
}
